import { Avatar, AvatarPicker } from "@/components/Avatar";
import ActivityBell, { FriendsButton } from "@/components/ActivityBell";
import { uploadPhoto } from "@/components/photoUpload";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Pencil } from "lucide-react";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

/** The username and short profile, at the top of My Theatre.
 *  profile: { username, profile_public } from the diary. onProfile gets the updated profile. suggest: a starting username. */
export default function ProfileBar({ profile, onProfile, suggest = "", onFriends, friendsActive = false }) {
  const [friendCount, setFriendCount] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/friends").then((r) => live && setFriendCount((r.data.friends || []).length)).catch(() => {});
    const h = (e) => setFriendCount(typeof e.detail === "number" ? e.detail : e.detail?.friends ?? null);
    window.addEventListener("mytheatre:friends", h);
    return () => { live = false; window.removeEventListener("mytheatre:friends", h); };
  }, []);
  const [name, setName] = useState(profile.username || suggest);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [about, setAbout] = useState(null);
  const [pic, setPic] = useState(false);
  const uploadAvatar = async (file) => {
    setPic(true);
    try {
      const v = await uploadPhoto(file, "avatar");
      setAbout((a) => ({ ...a, avatar: "", avatar_media_id: v.id, avatar_url: v.url }));
    } catch (err) { toast.error(errText(err, "Could not upload that photo. Please try another.")); }
    finally { setPic(false); }
  };
  const [lists, setLists] = useState([]);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    if (!profile.username) return;
    api.get("/about").then((r) => setAbout(r.data)).catch(() => setAbout({ bio: "", fav_show: "", first_show: "", avatar: "", pinned_list_id: "" }));
  }, [profile.username]);
  useEffect(() => {
    if (editing) api.get("/lists").then((r) => setLists(r.data.filter((l) => l.visibility !== "private"))).catch(() => setLists([]));
  }, [editing]);

  const saveName = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/diary-profile", { username: name, public: Boolean(profile.profile_public) });
      onProfile(r.data); setRenaming(false);
      toast.success("Username saved");
    } catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };
  const saveAbout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/about", { bio: about.bio, fav_show: about.fav_show, first_show: about.first_show, avatar: about.avatar || "", avatar_media_id: about.avatar_media_id || "", pinned_list_id: about.pinned_list_id || null });
      setAbout(r.data); setEditing(false);
      toast.success("Profile saved");
    } catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };

  if (profile.blocked) return null;

  if (!profile.username) {
    return (
      <section className="mb-6 rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-r from-[#1a1218] via-[#2a1620] to-[#1a1218] p-5" data-testid="choose-username">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-amber-50"><Users className="h-5 w-5 text-[#C89B3C]" /> Choose your username</h2>
        <p className="mt-1 text-sm text-amber-100/70">It's how friends find you, and what shows on your lists, notes and comments. Other members can search for it and send you a friend request (they see only your username, and you choose whether to accept). Your diary stays private until you decide otherwise.</p>
        <form onSubmit={saveName} className="mt-3 flex flex-wrap items-center gap-2">
          <input aria-label="Username" data-testid="pf-username-input" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} maxLength={20} placeholder="e.g. stagedoor-sam"
            className="w-56 rounded-full border border-border bg-background px-4 py-2 text-sm" />
          <button type="submit" disabled={busy || name.trim().length < 3} data-testid="pf-username-save" className="rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black disabled:opacity-50">{busy ? "Saving…" : "Choose username"}</button>
        </form>
        <p className="mt-2 text-xs text-amber-100/60">3 to 20 letters, numbers or hyphens.</p>
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-4" data-testid="profile-bar">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Avatar avatar={about?.avatar} url={about?.avatar_url} name={profile.username} />
          <div className="min-w-0">
            <p className="font-serif text-lg font-semibold leading-tight" data-testid="profile-name">{profile.username}</p>
            {about?.bio ? <p className="truncate text-sm text-muted-foreground" data-testid="profile-bio">{about.bio}</p> : <p className="text-sm text-muted-foreground">Add a short bio so friends know who you are.</p>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2" data-testid="profile-buttons">
          <FriendsButton active={friendsActive} onClick={onFriends} />
          <ActivityBell />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm">
        {friendCount !== null && (
          <button type="button" onClick={onFriends} data-testid="profile-friends" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Users className="h-3.5 w-3.5" /> {friendCount} friend{friendCount === 1 ? "" : "s"}</button>
        )}
        <Link to={`/u/${profile.username}`} data-testid="profile-view" className="text-[#C89B3C] underline">View my page</Link>
        <button type="button" onClick={() => setEditing(!editing)} data-testid="profile-edit" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /> {editing ? "Close" : "Edit profile"}</button>
      </div>

      {editing && about && (
        <form onSubmit={saveAbout} className="mt-4 space-y-3 border-t border-border pt-4" data-testid="profile-form">
          <div>
            <p className="mb-1 text-sm font-medium">Avatar</p>
            <AvatarPicker value={about.avatar || ""} photoUrl={about.avatar_url || ""} uploading={pic} onPhoto={uploadAvatar}
            onRemovePhoto={() => setAbout({ ...about, avatar_media_id: "", avatar_url: "" })}
            onChange={(a) => setAbout({ ...about, avatar: a, avatar_media_id: "", avatar_url: "" })} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="pf-bio">Bio <span className="font-normal text-muted-foreground">({about.bio.length} / 160)</span></label>
            <input id="pf-bio" data-testid="pf-bio" maxLength={160} value={about.bio} onChange={(e) => setAbout({ ...about, bio: e.target.value })} placeholder="e.g. Musical theatre addict, usually in the cheap seats" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="pf-fav">Favourite show</label>
              <input id="pf-fav" data-testid="pf-fav" maxLength={80} value={about.fav_show} onChange={(e) => setAbout({ ...about, fav_show: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="pf-first">First show I saw</label>
              <input id="pf-first" data-testid="pf-first" maxLength={80} value={about.first_show} onChange={(e) => setAbout({ ...about, first_show: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="pf-pin">Pinned list</label>
            <select id="pf-pin" data-testid="pf-pin" value={about.pinned_list_id || ""} onChange={(e) => setAbout({ ...about, pinned_list_id: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
              <option value="">None</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
            {lists.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Share a list with your friends (Lists tab) to be able to pin it.</p>}
          </div>
          <p className="text-xs text-muted-foreground">Your profile details are shown only to you and your accepted friends, on your page.</p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" disabled={busy} data-testid="pf-save" className="rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black disabled:opacity-60">Save profile</button>
            {!renaming && <button type="button" onClick={() => { setName(profile.username); setRenaming(true); }} data-testid="rename" className="text-sm text-muted-foreground underline">Change username</button>}
          </div>
        </form>
      )}
      {editing && renaming && (
        <form onSubmit={saveName} className="mt-3 flex flex-wrap items-center gap-2" data-testid="rename-form">
          <input aria-label="New username" data-testid="pf-username-input" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} maxLength={20} className="w-56 rounded-full border border-border bg-background px-4 py-2 text-sm" />
          <button type="submit" disabled={busy || name.trim().length < 3} data-testid="pf-username-save" className="rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black disabled:opacity-50">Save</button>
          <button type="button" onClick={() => setRenaming(false)} className="text-sm text-muted-foreground">Cancel</button>
          <p className="w-full text-xs text-muted-foreground">Links friends have to your page will stop working if you change it.</p>
        </form>
      )}
    </section>
  );
}